import numeral from 'numeral';

import { EOrderType } from '@webtrader/core/api/order';
import { checkIsOpenPosition, numeralToNumber } from '@webtrader/core/format/order';

import { checkIsNumber } from '../format/number';

interface ICalculateEstimatedTPorSLParams {
  ask: number;
  bid: number;
  orderType: EOrderType;
  pointStep: number;
  contractSize: number;
  tpOrSlPoints: number;
  lotSize?: number;
  userPrice?: number;
}

interface ICalculatePricePerLotSizeParams {
  pointStep: number;
  contractSize: number;
  lotSize?: number;
}

interface ICalculateEstimatedProfitLossParams {
  lotSize: number;
  pointStep: number;
  tpOrSlPoints: number;
  contractSize: number;
}

interface IPointsToPriceParams {
  input: number;
  points: number;
  orderType: EOrderType;
  pointStep: number;
}

interface IPriceToPointsParams {
  input: number;
  price: number;
  pointStep: number;
  neutral?: boolean;
}

interface ICalculateCopyToOrderParams {
  buy: number;
  sell: number;
  orderType: EOrderType;
  copiedStopLoss: number;
  copiedTakeProfit: number;
}

interface IRetrieveMinimumStopLossOrTakeProfitParams {
  currentPrice: number;
  inputPrice: number;
  orderType: EOrderType;
  pointStep: number;
  stopsLevel: number;
}

export const calculateStopLossTakeProfitPriceLimit = (params: IRetrieveMinimumStopLossOrTakeProfitParams) => {
  const { currentPrice, inputPrice, orderType, pointStep, stopsLevel } = params;

  const isLimitOrder = !checkIsOpenPosition(orderType);

  const liveStopLossPriceLimit = stopLossPointsToPrice({
    input: currentPrice,
    points: stopsLevel,
    orderType,
    pointStep
  });

  const staticStopLossPriceLimit = stopLossPointsToPrice({
    input: inputPrice,
    points: stopsLevel,
    orderType,
    pointStep
  });

  const liveTakeProfitPriceLimit = takeProfitPointsToPrice({
    input: currentPrice,
    points: 1,
    orderType,
    pointStep
  });

  const staticTakeProfitPriceLimit = takeProfitPointsToPrice({
    input: inputPrice,
    points: 1,
    orderType,
    pointStep
  });

  switch (orderType) {
    case EOrderType.BUY:
    case EOrderType.BUY_STOP:
    case EOrderType.BUY_LIMIT:
      return {
        stopLossRange: [0, isLimitOrder ? staticStopLossPriceLimit : liveStopLossPriceLimit],
        takeProfitRange: [isLimitOrder ? staticTakeProfitPriceLimit : liveTakeProfitPriceLimit]
      };
    case EOrderType.SELL:
    case EOrderType.SELL_STOP:
    case EOrderType.SELL_LIMIT:
      return {
        stopLossRange: [isLimitOrder ? staticStopLossPriceLimit : liveStopLossPriceLimit],
        takeProfitRange: [0, isLimitOrder ? staticTakeProfitPriceLimit : liveTakeProfitPriceLimit]
      };
    default:
      return {
        stopLossRange: [],
        takeProfitRange: []
      };
  }
};

export const stopLossPointsToPrice = (params: IPointsToPriceParams) => {
  const { input, points, orderType, pointStep } = params;

  const pricePerPoint = numeralToNumber(numeral(points).multiply(pointStep));

  if (pricePerPoint === undefined || (pricePerPoint && !checkIsNumber(pricePerPoint))) {
    return undefined;
  }

  switch (orderType) {
    case EOrderType.BUY:
    case EOrderType.BUY_STOP:
    case EOrderType.BUY_LIMIT:
      return numeralToNumber(numeral(input).subtract(pricePerPoint));
    case EOrderType.SELL:
    case EOrderType.SELL_STOP:
    case EOrderType.SELL_LIMIT:
      return numeralToNumber(numeral(input).add(pricePerPoint));
    default:
      return undefined;
  }
};

export const priceToPoints = (params: IPriceToPointsParams) => {
  const { input, price, neutral, pointStep } = params;

  const diff = numeralToNumber(numeral(input).subtract(price));

  if (!diff) {
    return undefined;
  }

  return numeralToNumber(numeral(neutral ? Math.abs(diff) : diff).divide(pointStep));
};

export const takeProfitPointsToPrice = (params: IPointsToPriceParams) => {
  const { input, points, orderType, pointStep } = params;

  const pricePerPoint = numeralToNumber(numeral(points).multiply(pointStep));

  if (!pricePerPoint) {
    return undefined;
  }

  switch (orderType) {
    case EOrderType.BUY:
    case EOrderType.BUY_STOP:
    case EOrderType.BUY_LIMIT:
      return numeralToNumber(numeral(input).add(pricePerPoint));
    case EOrderType.SELL:
    case EOrderType.SELL_STOP:
    case EOrderType.SELL_LIMIT:
      return numeralToNumber(numeral(input).subtract(pricePerPoint));
    default:
      return undefined;
  }
};

export const calculateEstimatedTakeProfitOrStopLoss = (params: ICalculateEstimatedTPorSLParams) => {
  const { ask, bid, contractSize, lotSize, pointStep, orderType, tpOrSlPoints, userPrice } = params;

  if (!lotSize) {
    return {
      estimatedStopLoss: undefined,
      estimatedTakeProfit: undefined
    };
  }

  // const pricePerPoint = lotSize * contractSize * pointStep * tpOrSlPoints;
  const pricePerPoint = contractSize * pointStep * tpOrSlPoints;

  switch (orderType) {
    case EOrderType.BUY:
      return {
        estimatedStopLoss: bid - pricePerPoint,
        estimatedTakeProfit: bid + pricePerPoint
      };
    case EOrderType.SELL:
      return {
        estimatedStopLoss: ask + pricePerPoint,
        estimatedTakeProfit: ask - pricePerPoint
      };
    case EOrderType.BUY_STOP:
    case EOrderType.BUY_LIMIT:
    case EOrderType.SELL_STOP:
    case EOrderType.SELL_LIMIT:
      return {
        estimatedStopLoss: userPrice ? userPrice - pricePerPoint : undefined,
        estimatedTakeProfit: userPrice ? userPrice + pricePerPoint : undefined
      };
    default:
      return {
        estimatedStopLoss: undefined,
        estimatedTakeProfit: undefined
      };
  }
};

export const calculatePricePerLotSize = (params: ICalculatePricePerLotSizeParams) => {
  const { lotSize, pointStep, contractSize } = params;

  if (!lotSize) {
    return undefined;
  }

  return numeralToNumber(numeral(lotSize).multiply(contractSize).multiply(pointStep));
};

export const calculateEstimatedProfitLoss = (params: ICalculateEstimatedProfitLossParams) => {
  const { lotSize, pointStep, contractSize, tpOrSlPoints } = params;

  return numeralToNumber(numeral(lotSize).multiply(contractSize).multiply(pointStep).multiply(tpOrSlPoints));
};

export const decimals = (value?: number) => {
  if (!value || Math.floor(value) === value) {
    return 0;
  }

  return value.toString().split('.')[1]?.length || 0;
};

export const calculateCopyToOrder = (params: ICalculateCopyToOrderParams) => {
  const { buy, sell, orderType, copiedStopLoss, copiedTakeProfit } = params;

  switch (orderType) {
    case EOrderType.BUY:
      return {
        stopLoss: copiedStopLoss > sell ? undefined : copiedStopLoss,
        takeProfit: copiedTakeProfit >= sell ? copiedTakeProfit : undefined
      };
    case EOrderType.SELL:
      return {
        stopLoss: copiedStopLoss >= buy ? copiedStopLoss : undefined,
        takeProfit: copiedTakeProfit > buy ? undefined : copiedTakeProfit
      };
    default:
      break;
  }

  return {
    stopLoss: undefined,
    takeProfit: undefined
  };
};

interface IStopLossAndTakeProfitPointsToPriceParams {
  price: number;
  orderType: EOrderType;
  pointStep: number;
  stopLoss?: number;
  takeProfit?: number;
}

export const stopLossAndTakeProfitPointsToPrice = (params: IStopLossAndTakeProfitPointsToPriceParams) => {
  const { price, stopLoss, takeProfit, orderType, pointStep } = params;

  let stopLossPrice = stopLoss;
  let takeProfitPrice = takeProfit;

  if (stopLoss) {
    stopLossPrice = stopLossPointsToPrice({
      input: price,
      points: stopLoss,
      orderType,
      pointStep
    });
  }

  if (takeProfit) {
    takeProfitPrice = takeProfitPointsToPrice({
      input: price,
      points: takeProfit,
      orderType,
      pointStep
    });
  }

  return {
    stopLossPrice,
    takeProfitPrice
  };
};
